import React, { useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';

// ---------------------------------------------------------------------------
// Paper Trackers  (route: /paper-trackers, alias: /print-forms)
//
// A teacher-facing printable-form generator. She (or a para, or a sub) can
// pick a template, optionally pick a student, and print a blank paper form
// to fill in by hand during the school day. Later she can enter what she
// jotted into the real app — this is a bridge, not a replacement.
//
// The three templates we ship first are the ones she and her paras asked
// for by name:
//
//   1. Weekly IEP goal data sheet — student's active goals as rows, five
//      school days as columns, boxes to mark Met / Partial / Not met.
//      Auto-fills the student name and the goal-area labels from her live
//      goals so it isn't a blank grid she has to hand-copy from Supabase.
//
//   2. ABC incident log — one blank behavior-incident form with structured
//      prompts for Antecedent / Behavior / Consequence + function + a
//      1-5 intensity scale to circle. No student data needed; a para can
//      grab a stack of these and use them cold.
//
//   3. Behavior tally sheet — a full-day grid of 30-minute time blocks x
//      behavior categories. Tally marks by hand. Optional student pre-fill.
//
// Every template is dead-simple ink on paper: no color, no gradients,
// bare minimum lines. The on-screen page has the app chrome (sidebar,
// header) for orientation; @media print hides that chrome so what
// comes out of the printer is the form itself, one sheet per template.
// ---------------------------------------------------------------------------

// Days of the week we render as column headers on the Weekly IEP goal
// data sheet. Kept short so five columns + a "Goal" column fit inside
// a US Letter page in portrait without shrinking the row height.
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// 30-minute time slots for the behavior tally sheet. School day span:
// 8:00 AM to 3:00 PM is 14 slots — that fits US Letter portrait with
// comfortable row heights.
const TIME_SLOTS = [
  '8:00', '8:30',
  '9:00', '9:30',
  '10:00', '10:30',
  '11:00', '11:30',
  '12:00', '12:30',
  '1:00', '1:30',
  '2:00', '2:30',
];

// Function-of-behavior tally columns. Match the same six functions the
// digital "Log Behavior" form uses so the paper sheet is a 1:1 backup.
const BEHAVIOR_FUNCTIONS = [
  { key: 'attention', label: 'Attention' },
  { key: 'escape',    label: 'Escape' },
  { key: 'tangible',  label: 'Tangible' },
  { key: 'sensory',   label: 'Sensory' },
  { key: 'automatic', label: 'Automatic' },
  { key: 'unknown',   label: 'Unknown' },
];

const TEMPLATE_KEYS = {
  WEEKLY_GOAL: 'weekly_goal',
  INCIDENT: 'incident_abc',
  TALLY: 'tally',
};

const TEMPLATES = [
  {
    id: TEMPLATE_KEYS.WEEKLY_GOAL,
    title: 'Weekly IEP goal tracker',
    subtitle:
      'Pick a student. Prints one page with their active goals as rows and Monday–Friday as columns. Mark M / P / N in each box.',
    icon: 'Target',
    needsStudent: true,
  },
  {
    id: TEMPLATE_KEYS.INCIDENT,
    title: 'Blank behavior incident (ABC) log',
    subtitle:
      'One printable form for one incident. Fill in the Antecedent, Behavior, Consequence, function, and intensity by hand.',
    icon: 'AlertTriangle',
    needsStudent: false,
  },
  {
    id: TEMPLATE_KEYS.TALLY,
    title: 'Behavior tally sheet',
    subtitle:
      'Grid of 30-minute time blocks × functions of behavior. Tally marks in each cell. Great for FBA data collection.',
    icon: 'Grid3x3',
    needsStudent: false,
  },
];

function displayName(student) {
  if (!student) return '';
  const first = student.firstName || '';
  const last = student.lastName || '';
  return `${first} ${last}`.trim();
}

const PaperTrackersPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const { students, loading: studentsLoading } = useStudents();
  const { goals, loading: goalsLoading } = useIEPGoals();

  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATE_KEYS.WEEKLY_GOAL);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  // Kept as-typed ISO date so <input type="date"> hydrates it cleanly. Default:
  // Monday of the current week. Not required — the templates render fine with
  // a blank date field that the teacher fills in by hand.
  const [weekStartDate, setWeekStartDate] = useState(() => {
    const now = new Date();
    const day = now.getDay(); // 0 Sun ... 6 Sat
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    return monday.toISOString().slice(0, 10);
  });

  const activeTemplate = useMemo(
    () => TEMPLATES.find((t) => t.id === selectedTemplate) || TEMPLATES[0],
    [selectedTemplate],
  );

  const selectedStudent = useMemo(() => {
    if (!selectedStudentId) return null;
    return (Array.isArray(students) ? students : []).find(
      (s) => s.id === selectedStudentId,
    ) || null;
  }, [students, selectedStudentId]);

  // Active goals for the selected student, filtered to `in_progress` (or
  // no-status, which some older rows have). Sorted with the most recently
  // updated first so the top of the printout is the goals the team is
  // actively working on.
  const studentGoals = useMemo(() => {
    if (!selectedStudentId) return [];
    const source = Array.isArray(goals) ? goals : [];
    return source
      .filter((g) => g.studentId === selectedStudentId)
      .filter((g) => !g.status || g.status === 'in_progress' || g.status === 'not_started')
      .slice(0, 8);
  }, [goals, selectedStudentId]);

  const studentOptions = useMemo(() => {
    const list = Array.isArray(students) ? students : [];
    return list
      .map((s) => ({ value: s.id, label: displayName(s) || 'Unnamed student' }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [students]);

  // Print date shown at the top of every template (US format). Uses the
  // browser's locale so it's culturally correct for the teacher; the
  // hand-written date field is separate and defaults blank.
  const generatedOn = useMemo(() => new Date().toLocaleDateString(), []);

  const needsStudentAndMissing = activeTemplate.needsStudent && !selectedStudent;

  const handlePrint = () => {
    if (typeof window !== 'undefined' && typeof window.print === 'function') {
      window.print();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* App chrome — hidden when printing */}
      <div className="print:hidden">
        <Header
          onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          isMenuOpen={isSidebarOpen}
        />
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
      </div>

      <main
        className={`transition-all duration-300 pt-16 print:pt-0 print:ml-0 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="p-4 sm:p-6 print:p-0">
          <div className="max-w-5xl mx-auto space-y-6 print:max-w-full print:space-y-0">
            <div className="print:hidden">
              <Breadcrumb
                customItems={[
                  { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
                  { label: 'Paper Trackers', path: null, icon: 'Printer', current: true },
                ]}
              />
            </div>

            <div className="print:hidden">
              <h1 className="text-2xl font-bold text-foreground">Paper trackers</h1>
              <p className="text-sm text-muted-foreground max-w-3xl mt-1">
                Print a blank tracking sheet to fill in by hand during the
                day (great for paras, subs, or days you're away from the
                iPad). Enter the data into the app later, or keep the paper
                copy in your student's folder for parent meetings.
              </p>
            </div>

            {/* Template picker ------------------------------------------- */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-3 print:hidden">
              {TEMPLATES.map((tpl) => {
                const isActive = tpl.id === selectedTemplate;
                return (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setSelectedTemplate(tpl.id)}
                    className={`text-left rounded-lg border p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                      isActive
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-card hover:border-primary hover:bg-primary/5'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name={tpl.icon} size={16} className="text-primary" />
                      <p className="font-semibold text-card-foreground">{tpl.title}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{tpl.subtitle}</p>
                  </button>
                );
              })}
            </section>

            {/* Options row (student + week + Print) ---------------------- */}
            <section className="rounded-lg border border-border bg-card p-4 print:hidden">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                {activeTemplate.needsStudent && (
                  <div className="sm:col-span-1">
                    <Select
                      label="Student"
                      required
                      placeholder={studentsLoading ? 'Loading…' : 'Choose a student'}
                      options={studentOptions}
                      value={selectedStudentId}
                      onChange={setSelectedStudentId}
                      searchable
                    />
                  </div>
                )}
                {selectedTemplate === TEMPLATE_KEYS.WEEKLY_GOAL && (
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1">
                      Week of (Monday)
                    </label>
                    <input
                      type="date"
                      value={weekStartDate}
                      onChange={(e) => setWeekStartDate(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Pre-fills the date under each column header.
                    </p>
                  </div>
                )}
                <div className="sm:col-start-3 sm:ml-auto flex gap-2">
                  <Button
                    variant="default"
                    onClick={handlePrint}
                    title={
                      needsStudentAndMissing
                        ? 'Prints a blank tracker — pick a student to auto-fill their goals.'
                        : 'Print this tracker'
                    }
                    className="min-h-12 min-w-[8rem]"
                  >
                    <Icon name="Printer" size={16} />
                    Print
                  </Button>
                </div>
              </div>

              {needsStudentAndMissing && (
                <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertCircle" size={14} className="mt-0.5 flex-shrink-0" />
                    <p>
                      No student picked — clicking <strong>Print</strong> will
                      still work, but the goal rows will be blank for you to
                      fill in by hand. Pick a student above to auto-fill their
                      active goals. (If a student doesn't appear, add them
                      under <strong>Students</strong> first.)
                    </p>
                  </div>
                </div>
              )}
              {selectedTemplate === TEMPLATE_KEYS.WEEKLY_GOAL &&
                selectedStudent &&
                !goalsLoading &&
                studentGoals.length === 0 && (
                <div className="mt-3 rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
                    <p>
                      <strong>{displayName(selectedStudent)}</strong> has no
                      active IEP goals in the app yet, so the sheet will
                      print with blank goal rows for you to fill in by hand.
                    </p>
                  </div>
                </div>
              )}
            </section>

            {/* Live preview / print target ------------------------------ */}
            <section className="rounded-lg border border-border bg-white p-6 sm:p-8 print:p-0 print:border-0 print:rounded-none print:bg-white">
              {selectedTemplate === TEMPLATE_KEYS.WEEKLY_GOAL && (
                <WeeklyGoalSheet
                  student={selectedStudent}
                  goals={studentGoals}
                  weekStartDate={weekStartDate}
                  generatedOn={generatedOn}
                />
              )}
              {selectedTemplate === TEMPLATE_KEYS.INCIDENT && (
                <IncidentAbcSheet generatedOn={generatedOn} />
              )}
              {selectedTemplate === TEMPLATE_KEYS.TALLY && (
                <BehaviorTallySheet
                  student={selectedStudent}
                  studentOptions={studentOptions}
                  onPickStudent={setSelectedStudentId}
                  selectedStudentId={selectedStudentId}
                  generatedOn={generatedOn}
                />
              )}
            </section>

            <div className="print:hidden rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
                <p>
                  Tip: after you fill in a paper sheet, open the same student
                  in <strong>IEP Goals</strong> or <strong>Log Behavior</strong>{' '}
                  in the app and enter the data. That way meetings and reports
                  pull from one source of truth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Print styles.
          Everything with `.print\\:hidden` is stripped when printing.
          @page picks US Letter portrait with a 0.5in margin — matches the
          existing PrintStudentSummary page so a district that prints a
          batch of forms gets consistent page geometry. */}
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0.5in;
          }
          html, body {
            background: #fff !important;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Weekly IEP goal data sheet
//
// Prints one page with:
//   Header       — student name, teacher line, week of, generated date
//   Legend       — M = Met, P = Partial, N = Not met (+ blank = no session)
//   Grid         — goals as rows, Mon-Fri as columns, each cell is a
//                  big-enough box for a single letter and a note underneath
//   Notes strip  — 4 lines at the bottom for weekly summary observations
// ---------------------------------------------------------------------------
const WeeklyGoalSheet = ({ student, goals, weekStartDate, generatedOn }) => {
  const startDate = useMemo(() => {
    if (!weekStartDate) return null;
    const d = new Date(`${weekStartDate}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [weekStartDate]);

  const columnDates = useMemo(() => {
    if (!startDate) return WEEKDAYS.map(() => '');
    return WEEKDAYS.map((_, idx) => {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + idx);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    });
  }, [startDate]);

  // Pad goal rows to 6 minimum so an empty-caseload printout still gives
  // the teacher room to write goals in by hand.
  const rows = useMemo(() => {
    const filled = goals.map((g) => ({
      id: g.id,
      area: g.goalArea || 'Goal area',
      specific: g.specific || '',
    }));
    while (filled.length < 6) {
      filled.push({ id: `blank-${filled.length}`, area: '', specific: '', blank: true });
    }
    return filled;
  }, [goals]);

  return (
    <div className="text-gray-900 print:text-black">
      <div className="flex items-baseline justify-between border-b-2 border-gray-800 pb-2 mb-3">
        <h2 className="text-lg font-bold">Weekly IEP goal data sheet</h2>
        <span className="text-xs">Printed {generatedOn}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm mb-3">
        <FieldLine label="Student" value={displayName(student)} width="w-full" />
        <FieldLine label="Teacher" value="" width="w-full" />
        <FieldLine
          label="Week of"
          value={
            startDate
              ? startDate.toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : ''
          }
          width="w-full"
        />
        <FieldLine label="School" value="" width="w-full" />
      </div>

      <div className="text-[11px] text-gray-700 mb-2">
        <strong>Legend:</strong> M = Met · P = Partial · N = Not met · blank = no session ·
        Circle intensity 1–5 in the note under the letter if useful.
      </div>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="border-2 border-gray-800 bg-gray-100 text-left px-2 py-1 w-[36%] align-top">
              Goal
            </th>
            {WEEKDAYS.map((day, idx) => (
              <th
                key={day}
                className="border-2 border-gray-800 bg-gray-100 text-center px-2 py-1 align-top"
              >
                <div className="font-bold">{day}</div>
                <div className="text-[10px] font-normal text-gray-600">
                  {columnDates[idx] || '___/___'}
                </div>
              </th>
            ))}
            <th className="border-2 border-gray-800 bg-gray-100 text-left px-2 py-1 align-top w-[16%]">
              Notes
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="border border-gray-800 align-top px-2 py-2">
                {row.blank ? (
                  <div className="h-12 leading-6 text-gray-400 text-xs">
                    _______________________
                  </div>
                ) : (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500">
                      {row.area}
                    </div>
                    <div className="text-xs leading-snug">
                      {row.specific || '—'}
                    </div>
                  </div>
                )}
              </td>
              {WEEKDAYS.map((day) => (
                <td
                  key={day}
                  className="border border-gray-800 align-top px-2 py-2 text-center"
                >
                  <div className="h-6" />
                  <div className="border-t border-dashed border-gray-400 pt-1 text-[10px] text-gray-500">
                    &nbsp;
                  </div>
                </td>
              ))}
              <td className="border border-gray-800 align-top px-2 py-2">
                <div className="h-12" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-700">
          Weekly summary / patterns noticed
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="border-b border-gray-700 h-6" />
        ))}
      </div>

      <div className="mt-3 text-[10px] text-gray-500">
        EduTracker Pro • Paper tracker v1 — enter these values in the app for
        reports & IEP evidence.
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ABC incident log — one blank incident on one sheet
// ---------------------------------------------------------------------------
const IncidentAbcSheet = ({ generatedOn }) => (
  <div className="text-gray-900 print:text-black">
    <div className="flex items-baseline justify-between border-b-2 border-gray-800 pb-2 mb-3">
      <h2 className="text-lg font-bold">Behavior incident (ABC) log</h2>
      <span className="text-xs">Printed {generatedOn}</span>
    </div>

    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm mb-3">
      <FieldLine label="Student" value="" width="w-full" />
      <FieldLine label="Observer" value="" width="w-full" />
      <FieldLine label="Date" value="" width="w-full" />
      <FieldLine label="Time started / ended" value="" width="w-full" />
      <FieldLine label="Location" value="" width="w-full" />
      <FieldLine label="Setting / activity" value="" width="w-full" />
    </div>

    <SectionBox title="A — Antecedent" hint="What happened right before? (task, transition, peer, denial, etc.)">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="border-b border-gray-700 h-6" />
      ))}
    </SectionBox>

    <SectionBox
      title="B — Behavior"
      hint="What did the student do? Observable & measurable ('threw pencil', not 'was angry')."
    >
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="border-b border-gray-700 h-6" />
      ))}
      <div className="mt-2 grid grid-cols-2 gap-4 text-xs">
        <FieldLine label="Intensity (circle)" value="1  2  3  4  5" width="w-full" />
        <FieldLine label="Duration (seconds)" value="" width="w-full" />
      </div>
    </SectionBox>

    <SectionBox
      title="C — Consequence"
      hint="What happened right after? Adult response and how the student reacted."
    >
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="border-b border-gray-700 h-6" />
      ))}
      <div className="mt-2 text-xs">
        <span className="font-semibold">Student reaction (circle one):</span>
        {'   '}Stopped  ·  Continued  ·  Intensified
      </div>
    </SectionBox>

    <div className="mt-4 rounded border-2 border-gray-800 p-3">
      <div className="text-xs font-semibold uppercase tracking-wide">
        Function of behavior (circle best guess — pick "Unknown" if unsure)
      </div>
      <div className="text-sm mt-1 leading-6">
        {BEHAVIOR_FUNCTIONS.map((fn, idx) => (
          <span key={fn.key}>
            <span className="inline-block px-2">{fn.label}</span>
            {idx < BEHAVIOR_FUNCTIONS.length - 1 ? ' · ' : ''}
          </span>
        ))}
      </div>
      <p className="text-[10px] text-gray-500 mt-1">
        Same behavior can have different functions in different students —
        same behavior + different function = opposite intervention.
      </p>
    </div>

    <div className="mt-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-700">
        Follow-up (planned next steps, whom to notify)
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="border-b border-gray-700 h-6" />
      ))}
    </div>

    <div className="mt-3 text-[10px] text-gray-500">
      EduTracker Pro • Paper tracker v1 — enter this incident in the app
      (Log Behavior) so it feeds the ABC + function-of-behavior analytics.
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Behavior tally sheet — full-day grid of time blocks × functions
// ---------------------------------------------------------------------------
const BehaviorTallySheet = ({ student, generatedOn }) => (
  <div className="text-gray-900 print:text-black">
    <div className="flex items-baseline justify-between border-b-2 border-gray-800 pb-2 mb-3">
      <h2 className="text-lg font-bold">Behavior tally sheet</h2>
      <span className="text-xs">Printed {generatedOn}</span>
    </div>

    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm mb-3">
      <FieldLine label="Student" value={displayName(student)} width="w-full" />
      <FieldLine label="Observer" value="" width="w-full" />
      <FieldLine label="Date" value="" width="w-full" />
      <FieldLine label="Setting" value="" width="w-full" />
    </div>

    <div className="text-[11px] text-gray-700 mb-2">
      <strong>How to use:</strong> add a tally mark (│) in the box that matches
      the time block and best-guess function each time the behavior occurs.
      Total each column at the end of the day.
    </div>

    <table className="w-full border-collapse text-sm">
      <thead>
        <tr>
          <th className="border-2 border-gray-800 bg-gray-100 text-left px-2 py-1 w-20">
            Time
          </th>
          {BEHAVIOR_FUNCTIONS.map((fn) => (
            <th
              key={fn.key}
              className="border-2 border-gray-800 bg-gray-100 text-center px-2 py-1"
            >
              {fn.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {TIME_SLOTS.map((slot) => (
          <tr key={slot}>
            <td className="border border-gray-800 px-2 py-1 text-xs font-medium">
              {slot}
            </td>
            {BEHAVIOR_FUNCTIONS.map((fn) => (
              <td key={fn.key} className="border border-gray-800 h-8" />
            ))}
          </tr>
        ))}
        <tr>
          <td className="border-2 border-gray-800 bg-gray-100 px-2 py-1 text-xs font-bold uppercase">
            Total
          </td>
          {BEHAVIOR_FUNCTIONS.map((fn) => (
            <td
              key={fn.key}
              className="border-2 border-gray-800 bg-gray-100 h-8 text-center"
            />
          ))}
        </tr>
      </tbody>
    </table>

    <div className="mt-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-700">
        Observations / triggers noticed
      </div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="border-b border-gray-700 h-6" />
      ))}
    </div>

    <div className="mt-3 text-[10px] text-gray-500">
      EduTracker Pro • Paper tracker v1 — the totals here feed into the
      behavior-trends report if you log the incidents in the app.
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
const FieldLine = ({ label, value, width = 'w-full' }) => (
  <div className={`${width} text-sm`}>
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 mr-2">
      {label}:
    </span>
    <span className="inline-block border-b border-gray-700 min-w-[10rem]">
      {value || '\u00A0'}
    </span>
  </div>
);

const SectionBox = ({ title, hint, children }) => (
  <div className="mt-4 rounded border-2 border-gray-800 p-3">
    <div className="flex items-baseline justify-between">
      <div className="text-sm font-bold">{title}</div>
      {hint && <div className="text-[11px] text-gray-600 ml-3">{hint}</div>}
    </div>
    <div className="mt-2">{children}</div>
  </div>
);

export default PaperTrackersPage;
